import { useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import { escribirDatosPrestamo } from '@actual-app/core/shared/prestamos';
import type { DatosPrestamo } from '@actual-app/core/shared/prestamos';
import {
  currencyToAmount,
  currencyToInteger,
} from '@actual-app/core/shared/util';

import { useCreateAccountMutation } from '#accounts';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { Icono } from '#components/mobile/ui/Icono';
import { color, texto } from '#components/mobile/ui/tokens';
import { useSyncedPref } from '#hooks/useSyncedPref';

import { cuotaFrancesa, escribirTipo, TIPOS_CUENTA } from './cuentas';
import type { TipoCuenta } from './cuentas';
import { iconoDeCuenta, useNombreTipo } from './FilaCuenta';

/** Por defecto: corriente en el presupuesto; lo demás, fuera. */
const FUERA_POR_DEFECTO: Record<TipoCuenta, boolean> = {
  corriente: false,
  ahorro: true,
  inversion: true,
  prestamo: true,
};

/**
 * Tipo de préstamo por el nombre. Si el nombre no lo dice, «otra deuda»
 * (Vadym, 8-oct-2026: mejor el aviso genérico «Completa los datos del
 * préstamo» que suponer un préstamo personal).
 */
function tipoPrestamo(nombre: string) {
  return /coche|cotxe|car\b|auto|moto/i.test(nombre)
    ? 'autoLoan'
    : /hipoteca|mortgage|casa|piso/i.test(nombre)
      ? 'mortgage'
      : /personal/i.test(nombre)
        ? 'personalLoan'
        : /estudios|student/i.test(nombre)
          ? 'studentLoan'
          : 'otherDebt';
}

/**
 * «Añadir cuenta»: en el presupuesto o fuera, tipo, nombre y saldo actual.
 * Un préstamo pide además interés, cuota y plazo (la cuota se calcula si
 * falta) y guarda su línea `#prestamo` en la nota. Crea la cuenta con la
 * acción real de Actual (`account-create`).
 */
export function HojaNuevaCuenta({
  onCerrar,
  onCreada,
  onConectarBanco,
  aviso,
  avisoBanco,
}: {
  onCerrar: () => void;
  /** Aviso arriba (B6 · H74): «Primero crea una cuenta». */
  aviso?: ReactNode;
  onCreada?: (id: string) => void;
  /** «Conectar con el banco»: crea o enlaza las cuentas desde el banco. Sin él no sale. */
  onConectarBanco?: () => void;
  /**
   * Sin banco todavía: tarjeta «en camino» con «Avísame». Solo si no hay
   * `onConectarBanco` (Rumbo, plan de 19-oct-2026).
   */
  avisoBanco?: { quiere: boolean; onCambiar: (activo: boolean) => void };
}) {
  const { t } = useTranslation();
  const nombreTipo = useNombreTipo();
  const crear = useCreateAccountMutation();
  const [tiposRaw, setTiposRaw] = useSyncedPref('cuentas-tipos');
  const [tipo, setTipo] = useState<TipoCuenta>('corriente');
  const [fuera, setFuera] = useState(false);
  const [nombre, setNombre] = useState('');
  const [saldo, setSaldo] = useState('');
  const [interes, setInteres] = useState('');
  const [cuota, setCuota] = useState('');
  const [plazo, setPlazo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const esPrestamo = tipo === 'prestamo';

  const guardar = async () => {
    const nombreLimpio = nombre.trim();
    if (!nombreLimpio) {
      setError(t('Give the account a name'));
      return;
    }
    const importe = saldo.trim() ? currencyToAmount(saldo) : 0;
    if (importe == null) {
      setError(t('The balance is not a valid amount'));
      return;
    }
    let datos: DatosPrestamo | null = null;
    if (esPrestamo) {
      const interesNum = interes.trim()
        ? Number(interes.replace(',', '.'))
        : null;
      if (interesNum == null || !Number.isFinite(interesNum)) {
        setError(t('Enter the nominal interest rate (TIN), for example 6.99'));
        return;
      }
      const anos = plazo.trim() ? Number(plazo.replace(',', '.')) : null;
      const meses = anos != null && anos > 0 ? Math.round(anos * 12) : null;
      let cuotaCent = cuota.trim() ? currencyToInteger(cuota) : null;
      if ((cuotaCent == null || cuotaCent <= 0) && meses) {
        cuotaCent = cuotaFrancesa(
          Math.round(Math.abs(importe) * 100),
          interesNum,
          meses,
        );
      }
      if (cuotaCent == null || cuotaCent <= 0) {
        setError(t('Enter the monthly payment or the term'));
        return;
      }
      datos = {
        tipo: tipoPrestamo(nombreLimpio),
        interes_anual: interesNum,
        cuota_minima: Math.abs(cuotaCent) / 100,
        desde: monthUtils.currentDay(),
        ...(meses ? { plazo_meses: meses } : {}),
      };
    }
    setError(null);
    setGuardando(true);
    try {
      const id = await crear.mutateAsync({
        name: nombreLimpio,
        // Un préstamo es deuda: el saldo se guarda en negativo.
        balance: esPrestamo ? -Math.abs(importe) : importe,
        offBudget: fuera,
      });
      setTiposRaw(escribirTipo(tiposRaw, id, tipo));
      if (datos) {
        await send('notes-save', {
          id: `account-${id}`,
          note: escribirDatosPrestamo(null, datos),
        });
      }
      onCreada?.(id);
      onCerrar();
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Hoja
      aria-label={t('Add account')}
      data-testid="nueva-cuenta"
      onCerrar={onCerrar}
    >
      <Text style={{ ...texto.titulo, color: color.fg, marginBottom: 12 }}>
        <Trans>Add account</Trans>
      </Text>
      {aviso && (
        <View
          role="status"
          data-testid="nueva-cuenta-aviso"
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            padding: '10px 12px',
            marginBottom: 12,
            borderRadius: 14,
            backgroundColor: color.warnSoft,
            color: color.warn,
            fontWeight: 600,
          }}
        >
          <Icono nombre="alert" size={18} />
          <Text style={{ flex: 1, color: color.fg }}>{aviso}</Text>
        </View>
      )}
      <View
        role="group"
        aria-label={t('Budget')}
        style={{
          flexDirection: 'row',
          gap: 2,
          padding: 3,
          borderRadius: 12,
          backgroundColor: color.surface2,
        }}
      >
        {[false, true].map(f => (
          <Button
            key={String(f)}
            variant="bare"
            aria-pressed={fuera === f}
            data-testid={f ? 'nueva-fuera' : 'nueva-dentro'}
            onPress={() => setFuera(f)}
            style={{
              flex: 1,
              height: 34,
              borderRadius: 9,
              fontSize: 13,
              fontWeight: 700,
              color: fuera === f ? color.fg : color.fg2,
              backgroundColor: fuera === f ? color.surface : 'transparent',
              boxShadow:
                fuera === f ? '0 1px 2px rgba(20,30,60,.12)' : undefined,
            }}
          >
            {f ? t('Off budget') : t('In the budget')}
          </Button>
        ))}
      </View>
      <View
        role="group"
        aria-label={t('Account type')}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 8,
          margin: '12px 0',
        }}
      >
        {TIPOS_CUENTA.map(k => {
          const on = k === tipo;
          return (
            <Button
              key={k}
              variant="bare"
              aria-pressed={on}
              data-testid={`nueva-tipo-${k}`}
              onPress={() => {
                setTipo(k);
                setFuera(FUERA_POR_DEFECTO[k]);
              }}
              style={{
                height: 70,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                borderRadius: 14,
                border: `1.5px solid ${on ? color.accent : color.line}`,
                backgroundColor: on ? color.accentSoft : 'transparent',
                color: on ? color.accent : color.fg2,
                fontSize: 12,
                fontWeight: 700,
                padding: 0,
              }}
            >
              <Icono nombre={iconoDeCuenta(k, '')} size={20} />
              {nombreTipo(k)}
            </Button>
          );
        })}
      </View>
      <View style={{ gap: 10 }}>
        <Campo etiqueta={t('Name')}>
          <CampoTexto
            valor={nombre}
            onChange={setNombre}
            etiqueta={t('Name')}
            testId="nueva-nombre"
            placeholder={
              esPrestamo ? t('e.g. Mortgage') : t('e.g. CaixaBank, Cash…')
            }
          />
        </Campo>
        <Campo
          etiqueta={esPrestamo ? t('Outstanding debt') : t('Current balance')}
        >
          <CampoTexto
            valor={saldo}
            onChange={setSaldo}
            etiqueta={esPrestamo ? t('Outstanding debt') : t('Current balance')}
            testId="nueva-saldo"
            placeholder="0,00"
            numerico
          />
        </Campo>
        {esPrestamo && (
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Campo etiqueta={t('TIN (%)')} flex>
              <CampoTexto
                valor={interes}
                onChange={setInteres}
                etiqueta={t('TIN (%)')}
                testId="nueva-interes"
                placeholder="2,15"
                numerico
              />
            </Campo>
            <Campo etiqueta={t('Instalment')} flex>
              <CampoTexto
                valor={cuota}
                onChange={setCuota}
                etiqueta={t('Instalment')}
                testId="nueva-cuota"
                placeholder="712,40"
                numerico
              />
            </Campo>
            <Campo etiqueta={t('Years')} flex>
              <CampoTexto
                valor={plazo}
                onChange={setPlazo}
                etiqueta={t('Term in years')}
                testId="nueva-plazo"
                placeholder="25"
                numerico
              />
            </Campo>
          </View>
        )}
        {/* Sin función (beta, quien no es admin) no se ofrece el banco. */}
        {onConectarBanco && (
          <Button
            variant="bare"
            data-testid="nueva-conectar-banco"
            onPress={() => {
              onCerrar();
              onConectarBanco();
            }}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 12,
              height: 48,
              padding: '0 12px',
              borderRadius: 12,
              border: `1px solid ${color.line2}`,
              color: color.accent,
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            <Icono nombre="refresh" size={18} />
            <span style={{ flex: 1, textAlign: 'left' }}>
              <Trans>Connect to the bank</Trans>
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: color.fg3 }}>
              <Trans>CaixaBank, Trade Republic…</Trans>
            </span>
          </Button>
        )}
        {!onConectarBanco && avisoBanco && (
          <View
            data-testid="nueva-banco-avisame"
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              minHeight: 48,
              padding: '0 6px 0 12px',
              borderRadius: 12,
              border: `1px solid ${color.line2}`,
            }}
          >
            <Icono nombre="bank" size={18} style={{ color: color.fg3 }} />
            <Text
              style={{
                flex: 1,
                fontSize: 14,
                fontWeight: 700,
                color: color.fg2,
              }}
            >
              <Trans>Connect to the bank: on its way</Trans>
            </Text>
            <Boton
              variante={avisoBanco.quiere ? 'fantasma' : 'primario'}
              zona={44}
              aria-pressed={avisoBanco.quiere}
              data-testid="nueva-banco-avisame-boton"
              onPress={() => avisoBanco.onCambiar(!avisoBanco.quiere)}
              style={{ minHeight: 34, padding: '0 12px', fontSize: 13 }}
            >
              {avisoBanco.quiere ? (
                <Trans>We will let you know ✓</Trans>
              ) : (
                <Trans>Notify me</Trans>
              )}
            </Boton>
          </View>
        )}
        {error && (
          <Text
            role="alert"
            style={{ color: color.bad, fontSize: 13, fontWeight: 600 }}
          >
            {error}
          </Text>
        )}
        <Boton
          bloque
          data-testid="nueva-guardar"
          isDisabled={guardando}
          onPress={() => void guardar()}
        >
          <Trans>Add account</Trans>
        </Boton>
      </View>
    </Hoja>
  );
}

function Campo({
  etiqueta,
  flex,
  children,
}: {
  etiqueta: string;
  flex?: boolean;
  children: ReactNode;
}) {
  return (
    <View style={{ gap: 5, flex: flex ? 1 : undefined, minWidth: 0 }}>
      <Text style={{ ...texto.etiqueta, color: color.fg3 }}>{etiqueta}</Text>
      {children}
    </View>
  );
}

function CampoTexto({
  valor,
  onChange,
  etiqueta,
  testId,
  placeholder,
  numerico,
}: {
  valor: string;
  onChange: (v: string) => void;
  etiqueta: string;
  testId: string;
  placeholder?: string;
  numerico?: boolean;
}) {
  return (
    <Input
      value={valor}
      aria-label={etiqueta}
      data-testid={testId}
      placeholder={placeholder}
      inputMode={numerico ? 'decimal' : undefined}
      onChangeValue={onChange}
      style={{
        height: 44,
        width: '100%',
        fontSize: 15,
        fontWeight: 600,
        borderRadius: 12,
        border: `1px solid ${color.line2}`,
        backgroundColor: color.surface,
        color: color.fg,
        padding: '0 12px',
      }}
    />
  );
}
